import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { formatApiError } from "../api";
import { Printer, Lock, Mail, ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@singhoffset.com");
  const [password, setPassword] = useState("Admin@123");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 paper-texture relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, #C1121F 1px, transparent 0)',
          backgroundSize: '24px 24px',
        }} />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <div className="brand-ribbon w-12 h-12 rounded flex items-center justify-center">
              <Printer className="text-white" size={24} strokeWidth={1.75} />
            </div>
            <div>
              <div className="font-display text-2xl font-bold tracking-tight text-[#0F172A]">SINGH OFFSET</div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#C1121F] font-semibold">Print · Press · Deliver</div>
            </div>
          </div>

          <div className="space-y-6 max-w-md">
            <div className="inline-block px-3 py-1 bg-white border border-[#E5E7EB] rounded-full text-xs font-mono-num uppercase tracking-wider text-[#475569]">
              Internal CRM · v1.0
            </div>
            <h1 className="font-display text-5xl font-bold leading-[1.05] text-[#0F172A]">
              Run your<br/>press like<br/>
              <span className="text-[#C1121F]">clockwork.</span>
            </h1>
            <p className="text-[#475569] text-base leading-relaxed">
              Track every job from quote to delivery. Manage hospitals, schools, and printing presses in one place — no more chasing paper trails.
            </p>
            <div className="flex gap-6 pt-4 text-xs uppercase tracking-wider text-[#475569]">
              <div><span className="text-[#C1121F] font-bold text-lg font-display block">120+</span> Job Types</div>
              <div><span className="text-[#C1121F] font-bold text-lg font-display block">∞</span> Customers</div>
              <div><span className="text-[#C1121F] font-bold text-lg font-display block">0</span> Lost Orders</div>
            </div>
          </div>

          <div className="text-xs text-[#94A3B8] font-mono-num">
            © {new Date().getFullYear()} Singh Offset · All jobs ship from Mathura.
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center bg-white px-6 py-12">
        <form onSubmit={onSubmit} className="w-full max-w-md page-enter" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="brand-ribbon w-10 h-10 rounded flex items-center justify-center">
              <Printer className="text-white" size={20} />
            </div>
            <div className="font-display text-xl font-bold">SINGH OFFSET</div>
          </div>

          <div className="text-xs uppercase tracking-[0.15em] text-[#C1121F] font-semibold mb-3">Sign in to continue</div>
          <h2 className="font-display text-3xl font-bold mb-2">Welcome back.</h2>
          <p className="text-[#475569] mb-8 text-sm">Enter your credentials to access the Singh Offset control room.</p>

          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-[#475569] mb-1.5 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={16} />
                <input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="input-base pl-9"
                  placeholder="admin@singhoffset.com"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-[#475569] mb-1.5 block">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={16} />
                <input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="input-base pl-9"
                  placeholder="••••••••"
                />
              </div>
            </div>
          </div>

          {error && (
            <div data-testid="login-error" className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="btn-primary w-full mt-6 py-3 rounded font-semibold flex items-center justify-center gap-2"
          >
            {loading ? "Signing in..." : (
              <>Sign in <ArrowRight size={16} /></>
            )}
          </button>

          <div className="mt-8 p-3 bg-[#F8F9FA] border border-[#E5E7EB] rounded text-xs text-[#475569]">
            <div className="font-mono-num">Demo · admin@singhoffset.com / Admin@123</div>
          </div>
        </form>
      </div>
    </div>
  );
}
